import { Injectable, Logger, OnModuleInit } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';

interface RegistryRow {
  schemaName: string;
  tableName: string;
}

/**
 * Which tables the database triggers already record for us.
 *
 * Read once at boot from `audit.audited_tables`, so enrolling a table is a data
 * change rather than a code change and the two halves cannot drift: the moment
 * a table gets a trigger, AuditService stops writing a duplicate application
 * row for it.
 *
 * Fails OPEN (nothing covered) rather than closed. If this table can't be read,
 * the application keeps writing change rows the old way — degraded but
 * complete, which is the right direction for an audit trail to fail in.
 */
@Injectable()
export class AuditTriggerRegistry implements OnModuleInit {
  private readonly logger = new Logger(AuditTriggerRegistry.name);
  private covered = new Set<string>();
  private loaded = false;

  constructor(private readonly prisma: PrismaService) {}

  async onModuleInit(): Promise<void> {
    await this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      const rows = await this.prisma.$queryRawUnsafe<RegistryRow[]>(
        `SELECT "schemaName", "tableName" FROM audit.audited_tables WHERE "enabled" = TRUE AND "tier" < 3`
      );

      this.covered = new Set(rows.map((r) => `${r.schemaName}.${r.tableName}`));
      this.loaded = true;
      this.logger.log(`audit triggers cover ${this.covered.size} table(s)`);
    } catch (error) {
      this.loaded = false;
      this.logger.warn(
        `could not read audit.audited_tables; application-side change logging stays on: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  isCovered(schemaName: string, tableName: string): boolean {
    if (!this.loaded) return false;
    return this.covered.has(`${schemaName}.${tableName}`);
  }

  coveredCount(): number {
    return this.covered.size;
  }
}
